import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partnerApi, type Partner } from '../lib/api';
import { SETTINGS_STRINGS } from '../lib/constants';
import { useToast } from './useToast';

/**
 * Hook to manage partner profile settings
 * Provides query and mutation for fetching and updating partner profile
 */
export function usePartnerProfile() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const query = useQuery({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
  });

  const mutation = useMutation({
    mutationFn: (data: Partial<Partner>) =>
      partnerApi.updatePartnerProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner'] });
      addToast({
        variant: 'success',
        title: SETTINGS_STRINGS.ORGANIZATION_PROFILE.SUCCESS_MESSAGE,
      });
    },
    onError: () => {
      addToast({
        variant: 'destructive',
        title: SETTINGS_STRINGS.ORGANIZATION_PROFILE.ERROR_MESSAGE,
      });
    },
  });

  return {
    partner: query.data,
    isLoading: query.isLoading,
    error: query.error,
    updateProfile: mutation.mutate,
    isUpdating: mutation.isPending,
  };
}
